
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import Footer from "@/components/layout/Footer";
import Navbar from "@/components/layout/Navbar";
import { 
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { translations, defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";

const Imprint = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);

  useEffect(() => {
    // Check saved language preference
    const savedLanguage = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (savedLanguage && ['en', 'de', 'fr', 'es'].includes(savedLanguage)) {
      setLanguage(savedLanguage as SupportedLanguage);
    } else {
      // If no saved language, detect browser language
      const browserLang = detectBrowserLanguage();
      setLanguage(browserLang);
      localStorage.setItem(LANGUAGE_COOKIE_NAME, browserLang);
    }
  }, []);
  
  const t = translations.legalPages[language]?.imprint;
  
  return (
    <>
      <Helmet>
        <title>
          {language === 'de' ? 'Impressum' : 
           language === 'fr' ? 'Mentions Légales' : 
           language === 'es' ? 'Aviso Legal' : 
           'Imprint'} - PaperVerse
        </title>
        <meta 
          name="description" 
          content={
            language === 'de' ? 'Rechtliche Informationen und Kontaktdaten für PaperVerse' : 
            language === 'fr' ? 'Informations légales et coordonnées pour PaperVerse' : 
            language === 'es' ? 'Información legal y datos de contacto para PaperVerse' : 
            'Legal information and contact details for PaperVerse'
          } 
        />
      </Helmet>

      <div className="min-h-screen flex flex-col">
        <Navbar />
        
        <main className="flex-1 max-w-4xl mx-auto w-full px-4 pt-24 pb-12">
          <div className="flex flex-wrap items-center justify-between mb-6">
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/">Home</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{t?.breadcrumb}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
            
            <div className="mt-2 sm:mt-0">
              <LanguageSelector language={language} onChange={setLanguage} />
            </div>
          </div>
          
          <h1 className="text-3xl font-bold tracking-tight mb-6">{t?.title}</h1>
          
          <div className="prose dark:prose-invert max-w-none">
            {t?.content.map((section, index) => (
              <div key={index}>
                <h2>{section.title}</h2>
                <p className="whitespace-pre-line">{section.text}</p>
              </div>
            ))}
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default Imprint;
